import { useAdaptiveRtStore } from '@/stores/adaptiveRtStore';
import { useCourseStore } from '@/stores/courseStore';
import { useHistoryRestoreStore } from '@/stores/historyRestoreStore';
import { isAdaptiveRtModule } from '@/utils/adaptiveRtModule';
import {
  buildAdaptiveRtDraftFromStore,
  applyAdaptiveRtDraftToStore,
  formatDraftRestoredMessage,
  storeHasPersistableDraftContent,
} from '@/utils/adaptiveRtDraft';
import {
  refreshAdaptiveRtWizardValidation,
  wizardValidationHasStructureMatrix,
} from '@/utils/refreshAdaptiveRtWizardValidation';
import {
  readAdaptiveRtDraft,
  writeAdaptiveRtDraft,
  clearAdaptiveRtDraft,
} from '@/utils/adaptiveRtDraftStorage';
import { normalizeCourseId, courseIdsEqual } from '@/utils/courseId';

let debounceTimer = null;
const DEBOUNCE_MS = 400;

/**
 * @param {number|string|null} courseId
 * @param {{ gatePassed?: boolean }} [opts]
 */
export function persistAdaptiveRtDraftNow(courseId, opts = {}) {
  const cid = normalizeCourseId(courseId);
  if (cid == null) return;

  const store = useAdaptiveRtStore();
  const draft = buildAdaptiveRtDraftFromStore(store, cid, opts);
  if (!draft) return;
  writeAdaptiveRtDraft(cid, draft);
}

export function scheduleAdaptiveRtDraftPersist(courseId, opts = {}) {
  const cid = normalizeCourseId(courseId);
  if (cid == null) return;

  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    persistAdaptiveRtDraftNow(cid, opts);
  }, DEBOUNCE_MS);
}

/**
 * @param {number|string|null} courseId
 * @param {{ skipIfStagesHavePlans?: boolean }} [opts]
 * @returns {{ restored: boolean, message?: string }|null}
 */
export async function tryRestoreAdaptiveRtDraft(courseId, opts = {}) {
  const cid = normalizeCourseId(courseId);
  if (cid == null) return null;

  if (useHistoryRestoreStore().record) {
    return null;
  }

  const draft = readAdaptiveRtDraft(cid);
  if (!draft || !courseIdsEqual(draft.courseId, cid)) return null;

  const store = useAdaptiveRtStore();
  if (opts.skipIfStagesHavePlans && store.planIds.length > 0) {
    return null;
  }

  applyAdaptiveRtDraftToStore(store, draft);

  const shouldRefreshGate =
    !!draft.gatePassedAt
    && store.canRunWizardValidate
    && (
      (draft.structure_aliases?.length > 0 && !wizardValidationHasStructureMatrix(store.wizardValidation))
      || !wizardValidationHasStructureMatrix(store.wizardValidation)
    );
  if (shouldRefreshGate) {
    try {
      await refreshAdaptiveRtWizardValidation(store);
    } catch {
      // validate на upload покажет ошибку при следующей проверке
    }
  }

  return {
    restored: true,
    message: formatDraftRestoredMessage(draft),
    gatePassed: !!draft.gatePassedAt,
  };
}

export function clearAdaptiveRtDraftForCourse(courseId) {
  clearAdaptiveRtDraft(courseId);
}

/**
 * Восстановить черновик, если выбран курс и активен модуль AdaptiveRT.
 * @param {{ skipIfStagesHavePlans?: boolean }} [opts]
 */
export async function restoreAdaptiveRtDraftForActiveCourse(opts = {}) {
  const courseStore = useCourseStore();
  const courseId = courseStore.selectedCourseId;
  if (!courseId || !isAdaptiveRtModule(courseStore.activeCalculationModuleId)) {
    return null;
  }
  return tryRestoreAdaptiveRtDraft(courseId, opts);
}

export { storeHasPersistableDraftContent };
